(function () {
  'use strict';

  /**
   * Slider configuration and utilities
   */
  const SliderConfig = {
    MOBILE_BREAKPOINT: 768,
    SCROLL_ANIMATION_DURATION: 500,
    LOOP_THRESHOLD: 0.5, // Threshold for detecting loop boundaries
  };

  /**
   * Get slider configuration from section data attributes
   */
  function getSliderConfig(section) {
    if (!section) return null;

    const isMobile = window.innerWidth < SliderConfig.MOBILE_BREAKPOINT;
    const layoutMode = section.dataset.layoutMode;
    
    // For care and ingredients, use columns_desktop if slides_per_view is not set
    let slidesPerView;
    if (isMobile) {
      slidesPerView = parseInt(section.dataset.slidesMobile || '1', 10);
      // Fallback to columns_mobile for care/ingredients if slides_per_view not set
      if (slidesPerView === 1 && (layoutMode === 'care_cards' || layoutMode === 'ingredients_grid')) {
        const columnsMobile = parseInt(
          getComputedStyle(section).getPropertyValue('--ns-grid-cols-mobile') || '1',
          10
        );
        slidesPerView = columnsMobile;
      }
    } else {
      slidesPerView = parseInt(section.dataset.slidesDesktop || '1', 10);
      // Fallback to columns_desktop for care/ingredients if slides_per_view not set
      if (slidesPerView === 1 && (layoutMode === 'care_cards' || layoutMode === 'ingredients_grid')) {
        const columnsDesktop = parseInt(
          getComputedStyle(section).getPropertyValue('--ns-grid-cols-desktop') || '3',
          10
        );
        slidesPerView = columnsDesktop;
      }
    }

    return {
      isMobile,
      slidesPerView,
      gap: parseInt(
        getComputedStyle(section.querySelector('.ns-multi-section__slider'))
          ?.getPropertyValue('--ns-grid-gap-x') || '16',
        10
      ),
    };
  }

  /**
   * Initialize infinite scroll slider
   */
  function initScrollSlider(sliderId) {
    const slider = document.getElementById(sliderId);
    if (!slider) return;

    // Prevent multiple initializations
    if (slider.dataset.initialized === 'true') {
      // Clean up and re-initialize
      const existingClones = slider.querySelectorAll('.ns-multi-section__slide--clone');
      existingClones.forEach(clone => clone.remove());
      slider.dataset.initialized = 'false';
    }

    const dots = Array.from(
      document.querySelectorAll(`page-dots[aria-controls="${sliderId}"] button`)
    );
    if (dots.length === 0) return;

    const section = slider.closest('.ns-multi-section');
    const config = getSliderConfig(section);
    if (!config) return;

    // Get original slides (excluding any existing clones)
    const allSlides = Array.from(slider.children);
    const originalSlides = allSlides.filter(
      slide => !slide.classList.contains('ns-multi-section__slide--clone')
    );
    const totalSlides = originalSlides.length;

    if (totalSlides === 0) return;

    // State management
    const state = {
      isScrolling: false,
      isProgrammaticScroll: false,
      scrollTimeout: null,
      rafId: null,
      realSlides: originalSlides,
      totalSlides,
      config,
    };

    /**
     * Setup infinite loop by cloning slides
     */
    function setupInfiniteLoop() {
      if (totalSlides <= 1) return;

      // Remove any existing clones first to prevent duplicates
      const existingClones = slider.querySelectorAll('.ns-multi-section__slide--clone');
      existingClones.forEach(clone => clone.remove());

      // Ensure we're working with the original slides (refresh reference after clone removal)
      const refreshedSlides = Array.from(slider.children);
      if (refreshedSlides.length !== totalSlides) {
        // If count doesn't match, something went wrong - use original reference
        state.realSlides = originalSlides;
      } else {
        state.realSlides = refreshedSlides;
      }

      // Clone the first and last ORIGINAL slides
      const firstOriginalSlide = state.realSlides[0];
      const lastOriginalSlide = state.realSlides[totalSlides - 1];
      
      const firstClone = firstOriginalSlide.cloneNode(true);
      const lastClone = lastOriginalSlide.cloneNode(true);
      
      firstClone.classList.add('ns-multi-section__slide--clone');
      lastClone.classList.add('ns-multi-section__slide--clone');
      
      // Insert clones: last clone before first slide, first clone after last slide
      slider.insertBefore(lastClone, firstOriginalSlide);
      slider.appendChild(firstClone);

      // Update real slides reference (excluding clones) - should be the same as originalSlides
      state.realSlides = Array.from(slider.children).filter(
        slide => !slide.classList.contains('ns-multi-section__slide--clone')
      );

      // Initialize scroll position to first real slide
      requestAnimationFrame(() => {
        const slideWidth = getSlideWidth();
        const offset = slideWidth;
        slider.scrollLeft = offset;
      });
    }

    /**
     * Get slide width including gap
     */
    function getSlideWidth() {
      return state.realSlides[0]?.offsetWidth || slider.clientWidth;
    }

    /**
     * Get slide dimensions
     */
    function getSlideDimensions() {
      const slideWidth = getSlideWidth();
      return {
        width: slideWidth,
        withGap: slideWidth + state.config.gap,
        offset: slideWidth, // Offset for cloned slide at start
      };
    }

    /**
     * Get current slide index based on scroll position
     */
    function getCurrentSlideIndex() {
      if (totalSlides <= 1) return 0;

      const { offset, withGap } = getSlideDimensions();
      const adjustedScroll = slider.scrollLeft - offset;
      const index = Math.round(adjustedScroll / withGap);

      return Math.max(0, Math.min(index, totalSlides - 1));
    }

    /**
     * Scroll to specific slide index
     */
    function scrollToSlide(targetIndex, smooth = true) {
      if (state.isScrolling || totalSlides <= 1 || targetIndex < 0 || targetIndex >= totalSlides) {
        return;
      }

      state.isScrolling = true;
      const { offset, withGap } = getSlideDimensions();
      const targetScroll = offset + targetIndex * withGap;

      slider.scrollTo({
        left: targetScroll,
        behavior: smooth ? 'smooth' : 'auto',
      });

      if (smooth) {
        clearTimeout(state.scrollTimeout);
        state.scrollTimeout = setTimeout(() => {
          state.isScrolling = false;
        }, SliderConfig.SCROLL_ANIMATION_DURATION);
      } else {
        state.isScrolling = false;
      }
    }

    /**
     * Handle infinite loop transitions
     */
    function handleLoop() {
      if (state.isScrolling || state.isProgrammaticScroll || totalSlides <= 1) return;

      const { offset, withGap } = getSlideDimensions();
      const scrollLeft = slider.scrollLeft;
      const totalWidth = offset + totalSlides * withGap;
      const threshold = offset * SliderConfig.LOOP_THRESHOLD;

      // Scrolled to cloned last slide at beginning
      if (scrollLeft <= threshold) {
        state.isProgrammaticScroll = true;
        slider.scrollLeft = offset + (totalSlides - 1) * withGap;
        requestAnimationFrame(() => {
          state.isProgrammaticScroll = false;
        });
      }
      // Scrolled to cloned first slide at end
      else if (scrollLeft >= totalWidth - threshold) {
        state.isProgrammaticScroll = true;
        slider.scrollLeft = offset;
        requestAnimationFrame(() => {
          state.isProgrammaticScroll = false;
        });
      }
    }

    /**
     * Handle pagination dot clicks with loop support
     */
    function handleDotClick(targetIndex) {
      if (totalSlides <= 1 || targetIndex < 0 || targetIndex >= totalSlides) return;

      const currentIndex = getCurrentSlideIndex();
      if (currentIndex === targetIndex) return;

      const { offset, withGap } = getSlideDimensions();

      // Handle loop transitions
      if (currentIndex === totalSlides - 1 && targetIndex === 0) {
        // Last to first: scroll to cloned first at end, then jump to start
        const totalWidth = offset + totalSlides * withGap;
        slider.scrollTo({ left: totalWidth, behavior: 'smooth' });
        setTimeout(() => {
          state.isProgrammaticScroll = true;
          slider.scrollTo({ left: offset, behavior: 'auto' });
          requestAnimationFrame(() => {
            state.isProgrammaticScroll = false;
          });
        }, SliderConfig.SCROLL_ANIMATION_DURATION);
      } else if (currentIndex === 0 && targetIndex === totalSlides - 1) {
        // First to last: scroll to cloned last at start, then jump to end
        slider.scrollTo({ left: 0, behavior: 'smooth' });
        setTimeout(() => {
          state.isProgrammaticScroll = true;
          slider.scrollTo({
            left: offset + (totalSlides - 1) * withGap,
            behavior: 'auto',
          });
          requestAnimationFrame(() => {
            state.isProgrammaticScroll = false;
          });
        }, SliderConfig.SCROLL_ANIMATION_DURATION);
      } else {
        scrollToSlide(targetIndex, true);
      }
    }

    /**
     * Update pagination dots based on current slide
     */
    function updateDots() {
      if (state.isScrolling) return;

      const currentIndex = getCurrentSlideIndex();
      dots.forEach((dot, index) => {
        if (index === currentIndex) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    /**
     * Handle scroll events with debouncing
     */
    function handleScroll() {
      if (state.rafId) {
        cancelAnimationFrame(state.rafId);
      }

      state.rafId = requestAnimationFrame(() => {
        handleLoop();
        updateDots();
      });
    }

    /**
     * Handle window resize
     */
    function handleResize() {
      // Recalculate config on resize
      const newConfig = getSliderConfig(section);
      if (newConfig) {
        state.config = newConfig;
      }
    }

    // Initialize
    setupInfiniteLoop();

    // Mark as initialized
    slider.dataset.initialized = 'true';

    // Attach event listeners
    dots.forEach((dot, index) => {
      dot.addEventListener('click', () => handleDotClick(index));
    });

    slider.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });

    // Initial update
    updateDots();
  }

  /**
   * Initialize all sliders on the page
   */
  function init() {
    const sliders = document.querySelectorAll(
      '.ns-multi-section__slider[id], .ns-multi-section__grid[id]'
    );
    
    sliders.forEach((slider) => {
      if (slider.id) {
        initScrollSlider(slider.id);
      }
    });
  }

  // Initialize on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-initialize on theme editor updates
  if (typeof Shopify !== 'undefined' && Shopify.designMode) {
    document.addEventListener('shopify:section:load', () => {
      setTimeout(init, 100);
    });
  }
})();
